import React, {Suspense, useCallback, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {HistoryDateProps} from '@di-shared/features/history/date/HistoryDate/index';
import HistoryDateCalendar from '@di-shared/features/history/date/HistoryDateCalendar';
import HistoryDateContent from '@di-shared/features/history/date/HistoryDateContent';
import useTripDayKr from '@di-shared/features/history/date/useTripDay.kr';
import {createHistoryFilterSearch, getDefaultHistoryFilterState} from '@di-shared/features/history/filter/historyFilterQuery.kr';
import HistoryLock from '@di-shared/features/history/setting/HistoryLock';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ClickAwayListener} from '@mui/base';
import {isSameDate, isSameYearAndMonth, startDateOfMonth} from '@shared/dateUtils';

export default function ({vin, date}: HistoryDateProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={date} />}>
      <Suspense fallback={<FallbackLoading />}>
        <HistoryLock
          vin={vin}
          fallbackLoading={<FallbackLoading />}
          fallbackError={<FallbackError selectedDate={date} />}
        >
          <Inner
            vin={vin}
            date={date}
          />
        </HistoryLock>
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, date}: HistoryDateProps) {
  const navigate = useNavigate();
  const {t} = useInferableTranslation();
  const {focusTripDate} = useTripDayKr({vin, selectedDate: date});
  const [innerDate, setInnerDate] = useState(focusTripDate);
  const [isMonthChange, setIsMonthChange] = useState(false);
  const [isMonthMode, setIsMonthMode] = useState(true);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  // 날짜 변경(월 이동 포함) 시 로컬 state와 함께 URL도 갱신해 브라우저 뒤로가기 시 히스토리 일관성 유지
  const navigateToDate = useCallback<React.Dispatch<React.SetStateAction<Date>>>(
    (value) => {
      const newDate = value instanceof Function ? value(innerDate) : value;
      setInnerDate(newDate);
      navigate(ROUTE_PATHS.HISTORY_$DATE.getFullPath(vin, getYYYYMMddFormat(newDate)), {replace: true});
    },
    [vin, navigate, innerDate]
  );

  return (
    <div className="wrap has_driving_sheet">
      <Header
        left={<HeaderBackButton />}
        right={
          <div className="nav_box">
            <button
              type="button"
              className="btn btn_ic"
              aria-label={t('T20414')}
              onClick={() => {
                navigate({
                  pathname: ROUTE_PATHS.HISTORY_FILTERED_FILTER.getFullPath(vin),
                  search: createHistoryFilterSearch({
                    ...getDefaultHistoryFilterState(),
                    sourceDate: innerDate,
                  }),
                });
              }}
            >
              <span
                className="ic ic_filter_20px"
                aria-hidden="true"
              />
            </button>
            <ClickAwayListener onClickAway={() => setIsMoreMenuOpen(false)}>
              <div className="btn_more_wrap">
                <button
                  type="button"
                  className="btn btn_ic"
                  aria-label={t('T20134')}
                  aria-expanded={isMoreMenuOpen}
                  aria-haspopup="menu"
                  aria-controls="history-more-menu"
                  onClick={() => setIsMoreMenuOpen((prev) => !prev)}
                >
                  <span
                    className="ic ic_menu_24px"
                    aria-hidden="true"
                  />
                </button>
                {isMoreMenuOpen && (
                  <ul
                    id="history-more-menu"
                    className="more_menu"
                    role="menu"
                  >
                    <li
                      role="none"
                      className="more_menu_item"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          navigate(ROUTE_PATHS.HISTORY_DOWNLOAD.getFullPath(vin));
                        }}
                      >
                        {t('T10089')}
                      </button>
                    </li>
                    <li
                      role="none"
                      className="more_menu_item"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setIsMoreMenuOpen(false);
                          navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
                        }}
                      >
                        {t('T20415')}
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </ClickAwayListener>
          </div>
        }
      >
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10098')}</span>
        </h1>
      </Header>
      <main
        className="content page_my_trips"
        id="content"
      >
        <div className={clsx('driving_sheet_fixed', {'is-collapse': !isMonthMode})}>
          <div className="trip_calendar">
            <HistoryDateCalendar
              vin={vin}
              isMonthMode={isMonthMode}
              selectedDate={innerDate}
              setSelectedDate={navigateToDate}
              isMonthChange={isMonthChange}
              setIsMonthChange={setIsMonthChange}
            />
          </div>
          <div className="driving_sheet_header">
            <button
              className="btn_drawer"
              onClick={() => setIsMonthMode((prev) => !prev)}
            >
              <span className="blind">{t('T10016')}</span>
            </button>
          </div>
        </div>
        <div className="driving_sheet_content">
          <HistoryDateContent
            vin={vin}
            selectedDate={innerDate}
            isMonthChange={isMonthChange}
          />
        </div>
      </main>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <div className="wrap has_driving_sheet">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10098')}</span>
        </h1>
      </Header>
      <main
        className="content page_my_trips"
        id="content"
      >
        <div className="driving_sheet_fixed">
          <div className="trip_calendar">
            <div className="react-datepicker">
              <div className="react-datepicker__month-container skeleton">
                <div className="react-datepicker__header">
                  <div className="react-datepicker__current-month skeleton">
                    <button
                      type="button"
                      className="react-datepicker__navigation react-datepicker__navigation--previous"
                      aria-label="Previous Month"
                    >
                      <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">Previous Month</span>
                    </button>
                    {CldrDatetimeFormatter.format(today, 'yMMM')}
                    <button
                      type="button"
                      className="react-datepicker__navigation react-datepicker__navigation--next navigation--disabled"
                      aria-label="Next Month"
                    >
                      <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">Next Month</span>
                    </button>
                  </div>
                  <div
                    className="react-datepicker__day-names"
                    style={{height: '260px'}}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="driving_sheet_header">
            <button className="btn_drawer">
              <span className="blind">{t('T10016')}</span>
            </button>
          </div>
        </div>
        <div className="driving_sheet_content">
          <div className="trip_summary">
            <div className="trip_date skeleton">
              <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
              <span className="trip_date_today">{t('T10008')}</span>
            </div>
            <div className="trip_summary_list type_multi_line type_error skeleton">
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10053')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('T10054')}</span>
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('T10055')}</span>
                </div>
              </div>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10052')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
                </div>
              </div>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10057')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('T10056')}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            className="trip_info skeleton"
            style={{height: '126px'}}
          >
            <span className="blind">trip info detail</span>
            <div className="empty_area">
              <span className="ic ic_caution_32px" />
              <span
                className="body_small_r"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T10175')}
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);
  const {defaultDistanceSymbol} = useUnitConverter();

  return (
    <div className="wrap has_driving_sheet">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10098')}</span>
        </h1>
      </Header>
      <main
        className="content page_my_trips"
        id="content"
      >
        <div className="driving_sheet_fixed">
          <div className="trip_calendar">
            <ReactDatePicker
              inline
              locale={getDateFnsLocale()}
              disabledKeyboardNavigation
              onChange={() => {}}
              selected={startDate}
              renderCustomHeader={({date}) => (
                <div className="react-datepicker__current-month">
                  <button
                    type="button"
                    aria-label="Previous Month"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
                  </button>
                  {CldrDatetimeFormatter.format(date, 'yMMM')}
                  <button
                    type="button"
                    aria-label="Next Month"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
                  </button>
                </div>
              )}
            />
          </div>
          <div className="driving_sheet_header">
            <button className="btn_drawer">
              <span className="blind">{t('T10016')}</span>
            </button>
          </div>
        </div>
        <div className="driving_sheet_content">
          <div className="trip_summary">
            <div className="trip_date">
              <span className="trip_date_text">{CldrDatetimeFormatter.format(startDate, 'MMMMEEEEd')}</span>
              {isSameDate(today, startDate) && <span className="trip_date_today">{t('T10008')}</span>}
            </div>
            <div className="trip_summary_list type_multi_line type_error">
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10053')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('T10054')}</span>
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('T10055')}</span>
                </div>
              </div>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10052')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
                </div>
              </div>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10057')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                  <span className="trip_summary_unit">{t('T10056')}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="trip_info">
            <span className="blind">trip info detail</span>
            <div className="empty_area">
              <span className="ic ic_caution_32px" />
              <span
                className="body_small_r"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T10175')}
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
